import {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FocusEventHandler,
  FunctionComponent,
  RefAttributes,
  useContext,
  useMemo,
  useRef,
} from 'react';
import ReactTextareaAutosize from 'react-textarea-autosize';

import { clstx } from '@payaca/utilities/cvaMerge';
import { FieldContext } from '../Field/Context';

export enum TextareaSizeVariant {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export enum TextareaStyleVariant {
  BASIC = 'basic',
  GREY = 'grey',
  UNDERLINE = 'underline',
}

export type TextareaProps = Omit<
  ComponentPropsWithoutRef<'textarea'>,
  'onChange' | 'name' | 'id' | 'style'
> & {
  sizeVariant?: TextareaSizeVariant;
  styleVariant?: TextareaStyleVariant;
  value?: string;
  onChange?: (value: string) => void;
  onTouch?: () => void;
  className?: string;
  changeTimeoutMs?: number;
  onChangeTimeout?: () => void;
  autoHeight?: boolean;
  maxRows?: number;
} & RefAttributes<HTMLTextAreaElement>;

export const Textarea: FunctionComponent<TextareaProps> = (props) => {
  const {
    ref,
    sizeVariant = TextareaSizeVariant.MD,
    styleVariant = TextareaStyleVariant.BASIC,
    value,
    onChange,
    onTouch,
    onChangeTimeout,
    changeTimeoutMs = 1000,
    disabled,
    className,
    onFocus,
    autoHeight = false,
    maxRows,
    ...rest
  } = props;

  const { id, name, validationState } = useContext(FieldContext);

  const changeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isUnderline = styleVariant === TextareaStyleVariant.UNDERLINE;

  const borderClasses = useMemo(() => {
    if (isUnderline) {
      return 'border-0 border-b-2 focus:ring-0';
    }

    return 'border focus:ring-1';
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'isUnderline'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: styleVariant
  }, [styleVariant]);

  const borderColourClasses = useMemo(() => {
    switch (validationState?.isValid) {
      case true:
        return 'border-green-500';
      case false:
        return 'border-red-500';
      default:
        return styleVariant === TextareaStyleVariant.GREY
          ? 'border-transparent'
          : 'border-gray-200';
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: isUnderline
  }, [validationState?.isValid, styleVariant, isUnderline]);

  const sizeClasses = useMemo(() => {
    switch (sizeVariant) {
      case TextareaSizeVariant.SM:
        return isUnderline ? 'pe-0 py-2 px-0' : 'py-2 px-3';
      case TextareaSizeVariant.MD:
        return isUnderline ? 'pe-0 py-3 px-0' : 'py-3 px-4';
      case TextareaSizeVariant.LG:
        return isUnderline ? 'pe-0 py-3 sm:py-5 px-0' : 'py-3 px-4 sm:p-5';
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'isUnderline'
  }, [sizeVariant]);

  const shapeClasses = useMemo(() => {
    if (isUnderline) {
      return 'rounded-none';
    }

    return 'rounded-lg';
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'isUnderline'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: styleVariant
  }, [styleVariant]);

  const backgroundColourClasses = useMemo(() => {
    switch (styleVariant) {
      case TextareaStyleVariant.GREY:
        return 'bg-gray-100';
      default:
        return 'bg-white';
    }
  }, [styleVariant]);

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    onChange?.(e.target.value);

    if (changeTimeout?.current) {
      clearTimeout(changeTimeout.current);
    }
    changeTimeout.current = setTimeout(() => {
      onChangeTimeout?.();
    }, changeTimeoutMs);
  };

  const handleTouch: FocusEventHandler<HTMLTextAreaElement> = (e) => {
    onTouch?.();
    // Normal on focus event
    onFocus?.(e);
  };

  const Element = autoHeight ? ReactTextareaAutosize : 'textarea';

  return (
    <Element
      ref={ref}
      id={id}
      name={name}
      value={value}
      className={clstx(
        'pl-form-textarea',
        'relative border-solid',
        'focus:border-blue-500 focus:ring-blue-500 focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-50',
        backgroundColourClasses,
        borderClasses,
        borderColourClasses,
        shapeClasses,
        sizeClasses,
        className
      )}
      disabled={disabled}
      onChange={handleChange}
      onFocus={handleTouch}
      rows={!autoHeight ? (rest.rows ?? 3) : undefined}
      minRows={autoHeight ? (rest.rows ?? 3) : undefined}
      maxRows={autoHeight ? maxRows : undefined}
      {...rest}
    />
  );
};
